import { defineMessages, useIntl } from 'react-intl';
import { sharedMessages } from '../sharedIntlLabels';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { Paper } from '@lifeomic/chroma-react/components/Paper';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Cpu, Database, FileText, PieChart, Zap } from '@lifeomic/chromicons';
import { BuildTableData } from '../FilterResultsManager/types';

import {
  getCKB,
  getGene,
  getAAChange,
  getNNChange,
  getZygosity,
  getPosition,
  getRef,
  getAlt,
  getVariantClass,
  getCodingEffect,
  getImpact,
  getDbsnpRsId,
  getExonNumber,
  getGeneId,
  getGeneClass,
  getTranscriptId,
  getVariantAlleleFreq,
  getRefAltDepth,
  getVcfFilter,
  getVcfQual,
  getIgv,
  getClinVarSignificance,
  getClinVarDisease,
  getClinVarId,
  getClinVarReview,
  getClinVarSubmission,
  getClinVarNearby,
  getCosmicCount,
  getCosmicHistology,
  getCosmicId,
  getCosmicNearby,
  getCosmicSite,
  getCosmicStatus,
  getDamagingPercentage,
  getDamagingRankScore,
  getFathmm,
  getMutTaster,
  getSift,
  getExomesFreq,
  getExomesHom,
  getGenomesFreq,
  getGenomesHom,
  getMaxFreq,
  getMinFreq,
} from '../FilterResultsManager/ShortVariantsContent/buildTableData';
import { useSharedDetailsStyles } from './sharedDetailsStyles';
import { useSharedStyles } from '../FilterResultsManager/sharedStyles';
import * as React from 'react';
import clsx from 'clsx';
import { getAminoAcidField } from '../utils';

const messages = defineMessages({
  summary: {
    id: 'omicsExplorer.variantDetails.dialog.variantDetails.shortVariantDetails.summary',
    defaultMessage: 'Summary',
  },
  vcfInfo: {
    id: 'omicsExplorer.variantDetails.dialog.variantDetails.shortVariantDetails.vcfInfo',
    defaultMessage: 'VCF Info',
  },
  clinVar: {
    id: 'omicsExplorer.variantDetails.dialog.variantDetails.shortVariantDetails.clinVar',
    defaultMessage: 'ClinVar',
  },
  cosmic: {
    id: 'omicsExplorer.variantDetails.dialog.variantDetails.shortVariantDetails.cosmic',
    defaultMessage: 'COSMIC',
  },
  inSilico: {
    id: 'omicsExplorer.variantDetails.dialog.variantDetails.shortVariantDetails.inSilico',
    defaultMessage: 'In Silico Prediction',
  },
  popAlleleFreq: {
    id: 'omicsExplorer.variantDetails.dialog.variantDetails.shortVariantDetails.popAlleleFreq',
    defaultMessage: 'Population Allele Frequency',
  },
});

interface ShortVariantDetailsProps
  extends Omit<
    BuildTableData,
    'classes' | 'formatMessage' | 'variantIds' | 'onSelectVariant'
  > {
  variant?: unknown;
}

export const ShortVariantDetails: React.FC<ShortVariantDetailsProps> = ({
  account,
  activeSequence,
  build,
  variant,
  variantSet,
}) => {
  const aminoAcidField = getAminoAcidField(account);
  const classes = useSharedDetailsStyles({});
  const sharedClasses = useSharedStyles({});

  const { formatMessage } = useIntl();

  return (
    <Box className={classes.root}>
      <Box direction="column">
        <Paper className={classes.paper}>
          <Box className={classes.subhead}>
            <Zap className={classes.icon} />
            <Text weight="bold">{formatMessage(messages.summary)}</Text>
          </Box>
          <dl>
            <dt>{formatMessage(sharedMessages.jax)}</dt>
            <dd>{getCKB(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.gene)}</dt>
            <dd>{getGene(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.aaChange)}</dt>
            <dd>{getAAChange(variant, aminoAcidField)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.nnChange)}</dt>
            <dd>{getNNChange(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.zygosity)}</dt>
            <dd>{getZygosity({ formatMessage, value: variant })}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.position)}</dt>
            <dd>{getPosition({ build, value: variant })}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.ref)}</dt>
            <dd>{getRef(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.alt)}</dt>
            <dd>{getAlt(variant)}</dd>
          </dl>
          <dl>
            <dt>{formatMessage(sharedMessages.variantClass)}</dt>
            <dd>{getVariantClass(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.codingEffect)}</dt>
            <dd>{getCodingEffect(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.impact)}</dt>
            <dd>{getImpact(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.dbsnpRsId)}</dt>
            <dd>{getDbsnpRsId(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.exonNumber)}</dt>
            <dd>{getExonNumber(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.geneId)}</dt>
            <dd>{getGeneId(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.geneClass)}</dt>
            <dd>{getGeneClass(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.transcriptId)}</dt>
            <dd>{getTranscriptId(variant)}</dd>
          </dl>
        </Paper>

        <Paper className={classes.paper}>
          <Box className={classes.subhead}>
            <FileText className={classes.icon} />
            <Text weight="bold">{formatMessage(messages.vcfInfo)}</Text>
          </Box>
          <dl>
            <dt>{formatMessage(sharedMessages.variantAlleleFreq)}</dt>
            <dd>{getVariantAlleleFreq(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.refAltDepth)}</dt>
            <dd>{getRefAltDepth(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.vcfFilter)}</dt>
            <dd>{getVcfFilter(variant)}</dd>
          </dl>
          <dl>
            <dt>{formatMessage(sharedMessages.vcfQual)}</dt>
            <dd>{getVcfQual(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.igv)}</dt>
            <dd>
              {getIgv({
                account,
                activeSequence,
                classes: sharedClasses,
                value: variant,
                variantSet,
                formatMessage,
              })}
            </dd>
          </dl>
        </Paper>

        <Paper className={classes.paper}>
          <Box className={classes.subhead}>
            <PieChart className={classes.icon} />
            <Text weight="bold">{formatMessage(messages.popAlleleFreq)}</Text>
          </Box>
          <dl>
            <dt>{formatMessage(sharedMessages.genomesFreq)}</dt>
            <dd>{getGenomesFreq({ build, value: variant })}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.genomesHom)}</dt>
            <dd>{getGenomesHom(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.exomesFreq)}</dt>
            <dd>{getExomesFreq({ build, value: variant })}</dd>
          </dl>
          <dl>
            <dt>{formatMessage(sharedMessages.exomesHom)}</dt>
            <dd>{getExomesHom(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.maxFreq)}</dt>
            <dd>{getMaxFreq(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.minFreq)}</dt>
            <dd>{getMinFreq(variant)}</dd>
          </dl>
        </Paper>
      </Box>

      <Box direction="column">
        <Paper className={clsx(classes.paper, classes.oneCol)}>
          <Box className={classes.subhead}>
            <Database className={classes.icon} />
            <Text weight="bold">{formatMessage(messages.clinVar)}</Text>
          </Box>
          <dl>
            <dt>{formatMessage(sharedMessages.clinVarSignificance)}</dt>
            <dd>{getClinVarSignificance(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.clinVarDisease)}</dt>
            <dd>{getClinVarDisease(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.clinVarId)}</dt>
            <dd>{getClinVarId(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.clinVarReview)}</dt>
            <dd>{getClinVarReview(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.clinVarSubmission)}</dt>
            <dd>{getClinVarSubmission(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.clinVarNearby)}</dt>
            <dd>{getClinVarNearby(variant)}</dd>
          </dl>
        </Paper>

        <Paper className={clsx(classes.paper, classes.oneCol)}>
          <Box className={classes.subhead}>
            <Database className={classes.icon} />
            <Text weight="bold">{formatMessage(messages.cosmic)}</Text>
          </Box>
          <dl>
            <dt>{formatMessage(sharedMessages.cosmicCount)}</dt>
            <dd>{getCosmicCount(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.cosmicStatus)}</dt>
            <dd>{getCosmicStatus(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.cosmicHistology)}</dt>
            <dd>{getCosmicHistology(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.cosmicSite)}</dt>
            <dd>{getCosmicSite(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.cosmicId)}</dt>
            <dd>{getCosmicId(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.cosmicNearby)}</dt>
            <dd>{getCosmicNearby(variant)}</dd>
          </dl>
        </Paper>

        <Paper className={classes.paper}>
          <Box className={classes.subhead}>
            <Cpu className={classes.icon} />
            <Text weight="bold">{formatMessage(messages.inSilico)}</Text>
          </Box>
          <dl>
            <dt>{formatMessage(sharedMessages.damagingPercentage)}</dt>
            <dd>{getDamagingPercentage(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.damagingRankScore)}</dt>
            <dd>{getDamagingRankScore(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.fathmm)}</dt>
            <dd>{getFathmm(variant)}</dd>
          </dl>
          <dl>
            <dt>{formatMessage(sharedMessages.mutTaster)}</dt>
            <dd>{getMutTaster(variant)}</dd>
            <Divider className={classes.divider} />
            <dt>{formatMessage(sharedMessages.sift)}</dt>
            <dd>{getSift(variant)}</dd>
          </dl>
        </Paper>
      </Box>
    </Box>
  );
};
